// The library shelf's book ORDER — the one thing about the shelf an admin can
// change without a deploy. `src/reader/shelf.ts` owns everything else the shelf
// knows (which covers a book ships as, what they are titled, what chapters each
// binds) as static presentation config; this is the single field lifted out of
// it into Convex, because "which book stands first" is an editorial decision
// someone makes on a Tuesday, not a code change.
//
// Under convex/model/ because BOTH sides need it, and must agree: the mutation
// validates a submitted order with `checkShelfOrder`, and the library applies
// the stored one with `shelfBookOrder` at render time. One implementation, so a
// list the server accepted can never be a list the reader silently drops.
//
// A BOOK order, never an EDITION order — a real constraint, not a shortcut.
// `shelfUnits` divides a book's reading order across its volumes assuming they
// arrive "in shelf order, ranges ascending"; an edition-level reorder could put
// the Odyssey's volume three ahead of volume one and hand each cover the wrong
// chapters, silently. Reordering whole books moves a book's volumes together,
// so that assumption survives by construction.

/** Outcome of validating a submitted order — errors as values (FP policy). */
export type ShelfOrderCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Is `requested` a legal shelf order over `known`?
 *
 * Two rules only: every slug must name a configured book, and no slug may
 * appear twice. COMPLETENESS is deliberately not one of them — a stored order
 * is a snapshot of the catalog as it stood when an admin pressed save, and the
 * next book added to `BOOK_SLUGS` makes every such snapshot partial. Rejecting
 * partial lists would mean the shelf order breaks the day a book ships;
 * `shelfBookOrder` appends the unmentioned books instead.
 *
 * Duplicates ARE rejected rather than deduped, because they are the one shape
 * that means the caller has a bug: `shelfBookOrder` would drop the repeat and
 * the admin would see a list they did not submit.
 */
export function checkShelfOrder(
  requested: readonly string[],
  known: readonly string[],
): ShelfOrderCheck {
  const knownSet = new Set(known);
  const errors = [
    ...requested
      .filter((slug) => !knownSet.has(slug))
      .map((slug) => `unknown book slug: ${slug}`),
    ...requested
      .filter((slug, i) => requested.indexOf(slug) !== i)
      .map((slug) => `duplicate book slug: ${slug}`),
  ];
  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

/**
 * The order to render `known` books in, given the stored override (`null` /
 * `undefined` = none stored, or not loaded yet — both mean "ship the default").
 *
 * TOTAL over any stored value, which is the point: the array comes back from
 * the database and may name a book that has since been removed from the config,
 * omit one that has since been added, or (with two admins saving at once) hold
 * a duplicate. None of those may drop a book off the shelf or render one twice,
 * so the merge is: the stored slugs that are still real, in stored order,
 * followed by every remaining known book in its default order.
 */
export function shelfBookOrder(
  known: readonly string[],
  stored: readonly string[] | null | undefined,
): readonly string[] {
  if (stored === null || stored === undefined) return known;
  const knownSet = new Set(known);
  const kept = stored.filter(
    (slug, i) => knownSet.has(slug) && stored.indexOf(slug) === i,
  );
  const keptSet = new Set(kept);
  return [...kept, ...known.filter((slug) => !keptSet.has(slug))];
}
